import { useState, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoginRequestSchema, type LoginRequest } from '@ctms/contracts';
import { AlertCircle, Lock, LogIn, Mail } from 'lucide-react';

import { authApi } from '../auth.api.js';
import { getApiErrorMessage } from '../../../api/client.js';

export interface LoginFormProps {
  onSuccess: (token: string) => void;
  onMfaRequired: (ticket: string, expiresAt: string) => void;
}

export function LoginForm({ onSuccess, onMfaRequired }: LoginFormProps): JSX.Element {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginRequest>({
    resolver: zodResolver(LoginRequestSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginRequest): Promise<void> => {
    setErrorMessage(null);
    setIsSubmitting(true);
    try {
      const response = await authApi.login(data);
      if (response.status === 'authenticated') {
        onSuccess(response.accessToken);
      } else if (response.status === 'mfa_required') {
        onMfaRequired(response.mfaTicket, response.expiresAt);
      }
    } catch (err: unknown) {
      const msg = await getApiErrorMessage(err, 'Nieprawidłowy adres e-mail lub hasło.');
      setErrorMessage(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-5"
      noValidate
      aria-label="Formularz logowania"
    >
      {errorMessage && (
        <div
          role="alert"
          className="flex items-center gap-2.5 rounded-md border border-rose-800/60 bg-rose-950/40 p-3 text-sm text-rose-300"
        >
          <AlertCircle className="h-4 w-4 shrink-0 text-rose-400" />
          <span>{errorMessage}</span>
        </div>
      )}

      <div>
        <label htmlFor="login-email" className="block text-xs font-medium uppercase tracking-wider text-slate-300">
          Adres e-mail
        </label>
        <div className="relative mt-1.5">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-500">
            <Mail className="h-4 w-4" />
          </div>
          <input
            id="login-email"
            type="email"
            autoComplete="email"
            disabled={isSubmitting}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? 'login-email-error' : undefined}
            placeholder="uzytkownik@osrodek.pl"
            className="w-full rounded-md border border-slate-700 bg-slate-900 py-2.5 pr-3 pl-9 text-sm text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 focus:outline-none disabled:opacity-50"
            {...register('email')}
          />
        </div>
        {errors.email && (
          <p id="login-email-error" className="mt-1 text-xs text-rose-400">
            {errors.email.message}
          </p>
        )}
      </div>

      <div>
        <label htmlFor="login-password" className="block text-xs font-medium uppercase tracking-wider text-slate-300">
          Hasło dostępu
        </label>
        <div className="relative mt-1.5">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-500">
            <Lock className="h-4 w-4" />
          </div>
          <input
            id="login-password"
            type="password"
            autoComplete="current-password"
            disabled={isSubmitting}
            aria-invalid={Boolean(errors.password)}
            aria-describedby={errors.password ? 'login-password-error' : undefined}
            placeholder="••••••••••••"
            className="w-full rounded-md border border-slate-700 bg-slate-900 py-2.5 pr-3 pl-9 text-sm text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 focus:outline-none disabled:opacity-50"
            {...register('password')}
          />
        </div>
        {errors.password && (
          <p id="login-password-error" className="mt-1 text-xs text-rose-400">
            {errors.password.message}
          </p>
        )}
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="flex w-full items-center justify-center gap-2 rounded-md bg-cyan-600 py-2.5 px-4 text-sm font-medium text-white transition-colors hover:bg-cyan-500 focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 focus:ring-offset-slate-950 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
      >
        <LogIn className="h-4 w-4" />
        {isSubmitting ? 'Logowanie…' : 'Zaloguj się'}
      </button>
    </form>
  );
}
